import { TypedUseSelectorHook, useDispatch as useReduxDispatch, useSelector as useReduxSelector } from 'react-redux';

import { connectInstanceApi } from '@api/connectInstance';
import { Middleware, configureStore } from '@reduxjs/toolkit';

import { identityApi, lockersApi, receivedDigitalApi } from './api';
import appReducer, { setSelectInstallationDialog } from './slices/app';
import authReducer from './slices/auth';
import mailpieceActionReducer from './slices/mailpieceAction';
import pieceSearchReducer from './slices/piceSearch';
import recipientFilterReducer from './slices/recipientFilter';

const connectInstancesListener: Middleware = (storeAPI) => (next) => (action) => {
  const result = next(action);

  if (connectInstanceApi.endpoints.getInstances.matchFulfilled(action)) {
    try {
      const instances = action.payload;
      const state = storeAPI.getState();

      const getMyProfileEndpoint = (receivedDigitalApi.endpoints as any).getMyProfile;
      const userQueryState = getMyProfileEndpoint.select()(state);
      const currentUserId = userQueryState?.data?.id;
      const currentUserConnected = instances.some(
        (inst) => inst?.connectedUserId && inst.connectedUserId === currentUserId,
      );

      if (!currentUserConnected && instances?.length > 0) {
        storeAPI.dispatch(
          setSelectInstallationDialog({
            open: true,
            instanceId: instances[0]?.id,
            controllingUserId: instances[0]?.connectedUserId,
          }),
        );
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error in connectInstancesListener:', error);
      }
    }
  }

  return result;
};

const store = configureStore({
  reducer: {
    auth: authReducer,
    pieceSearch: pieceSearchReducer,
    app: appReducer,
    mailpieceAction: mailpieceActionReducer,
    recipientFilter: recipientFilterReducer,
    [receivedDigitalApi.reducerPath]: receivedDigitalApi.reducer,
    [lockersApi.reducerPath]: lockersApi.reducer,
    [identityApi.reducerPath]: identityApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      receivedDigitalApi.middleware,
      lockersApi.middleware,
      identityApi.middleware,
      connectInstancesListener,
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

const { dispatch } = store;

// TODO: Move this out to a separate 'hooks.tsx' file
// to avoid potential circular import dependency issues.
const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;
const useDispatch = () => useReduxDispatch<AppDispatch>();

export { store, dispatch, useSelector, useDispatch };
